import api from './api';

export interface ProductProperty {
  id: number;
  title: string;
  type: string;
}

export interface ProductPropertyValue {
  id: number;
  title: string;
  value: string;
}

export interface ProductVariantProperty {
  property: ProductProperty;
  value: ProductPropertyValue;
}

export interface ProductVariant {
  id: number;
  price: number; // in tomans
  stock: number;
  properties: ProductVariantProperty[];
}

export interface ProductDetails {
  id: number;
  title: string;
  price: number; // in tomans
  variants: ProductVariant[];
}

// Full product details interfaces
export interface VendorInfo {
  id: number;
  name: string;
  identifier?: string;
  photo?: string;
  city?: string;
  province?: string;
  rating?: number;
  reviews?: number;
}

export interface ReviewUser {
  id: number;
  name: string;
  photo?: string;
}

export interface ProductReview {
  id: number;
  star: number;
  description: string;
  created_at: string;
  user: ReviewUser;
  like_count: number;
  dislike_count: number;
  photos: string[];
}

export interface ProductBadge {
  id: number;
  title: string;
  type: string;
  assign_description?: string;
  icon?: string;
  badge?: string;
}

export interface FullProductDetails {
  id: number;
  title: string;
  link: string;
  description: string;
  price: number;
  rating: number;
  reviews: number;
  photo: string;
  photos: string[];
  vendor: VendorInfo;
  city: string;
  province: string;
  inventory: number;
  variants: ProductVariant[];
  user_reviews: ProductReview[];
  social_proof_badges: ProductBadge[];
  badges: ProductBadge[];
}

export interface SimpleProduct {
  id: number;
  title: string;
  price: number;
  photo: string;
  vendor_name: string;
  vendor_identifier: string;
  rating: number;
  reviews: number;
}

export const productService = {
  async getProductDetails(productId: number): Promise<ProductDetails | null> {
    try {
      const { data } = await api.get<ProductDetails>(`/api/products/${productId}`);
      
      return {
        id: data.id,
        title: data.title,
        price: data.price,
        variants: (data.variants || []).map((variant: any) => ({
          id: variant.id,
          price: variant.price,
          stock: variant.stock,
          properties: (variant.properties || []).map((prop: any) => ({
            property: {
              id: prop.property.id,
              title: prop.property.title,
              type: prop.property.type,
            },
            value: {
              id: prop.value.id,
              title: prop.value.title,
              value: prop.value.value,
            },
          })),
        })),
      };
    } catch (error: any) {
      console.error('Error fetching product details:', error);
      return null;
    }
  },

  async getFullProductDetails(productId: number): Promise<FullProductDetails | null> {
    try {
      const { data } = await api.get<FullProductDetails>(`/api/products/${productId}/full`);
      return data;
    } catch (error: any) {
      console.error('Error fetching full product details:', error);
      return null;
    }
  },

  async getProductReviews(productId: number, limit: number = 5, offset: number = 0): Promise<{
    reviews: ProductReview[];
    total_count: number;
    has_next: boolean;
  } | null> {
    try {
      const { data } = await api.get(`/api/products/${productId}/reviews`, {
        params: { limit, offset }
      });
      return data;
    } catch (error: any) {
      console.error('Error fetching product reviews:', error);
      return null;
    }
  },

  async getSimilarProducts(productId: number, size: number = 4, offset: number = 0): Promise<SimpleProduct[]> {
    try {
      const { data } = await api.get<SimpleProduct[]>(`/api/products/${productId}/similar`, {
        params: { size, offset }
      });
      return data;
    } catch (error: any) {
      console.error('Error fetching similar products:', error);
      return [];
    }
  },

  async getVendorProducts(productId: number, size: number = 4, offset: number = 0): Promise<SimpleProduct[]> {
    try {
      const { data } = await api.get<SimpleProduct[]>(`/api/products/${productId}/vendor-products`, {
        params: { size, offset }
      });
      return data;
    } catch (error: any) {
      console.error('Error fetching vendor products:', error);
      return [];
    }
  },
};

